import { useEffect, useCallback, useState, type FC } from 'react'
import { useRouter } from 'next/router'

import { Badge } from '@wartek-id/badge'

import { Header } from 'app/KinerjaShared/components/Header'
import { InternalServerError } from 'utils/customError'
import { usePelaksanaanPraktikKinerjaPS } from 'app/KinerjaPSPegawaiPage/hooks/usePelaksanaanPraktikKinerjaPS'
import { PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import CardForm from 'app/KinerjaPSPegawaiPage/pages/Pelaksanaan/PraktikKinerja/PendampinganSatuanPendidikan/components/CardForm'
import OnceLoaded from 'app/KinerjaPSPegawaiPage/pages/Pelaksanaan/PraktikKinerja/components/OnceLoaded'
import CardFormPreview from 'app/KinerjaPSPegawaiPage/pages/Pelaksanaan/PraktikKinerja/PendampinganSatuanPendidikan/components/CardFormPreview'
import { usePPSTrackEvent } from 'app/KinerjaPSPegawaiPage/hooks/usePPSTrackEvent'
import usePPSPeriodGuard from 'app/KinerjaPSPegawaiPage/hooks/usePPSPeriodGuard'
import LoadingSpinner from 'app/KinerjaPSPegawaiPage/pages/Pelaksanaan/PraktikKinerja/components/LoadingSpinner'

import type {
  TPraktikKinerjaFormName,
  TPraktikKinerjaPendampinganFormValue,
} from 'types/kinerja-ps-pegawai'

type PendampinganSatuanPendidikanProps = {}

const formName: TPraktikKinerjaFormName = 'pendampingan_sekolah'

const PendampinganSatuanPendidikan: FC<
  PendampinganSatuanPendidikanProps
> = () => {
  const router = useRouter()
  const { trackEvent } = usePPSTrackEvent()
  const { isPeriodSuccess, pelaksanaanGuard } = usePPSPeriodGuard()

  const [isPageLoading, setPageLoading] = useState(true)

  // Entry Point Guards

  // Prepare form values from the API
  const formValues =
    usePelaksanaanPraktikKinerjaPS<TPraktikKinerjaPendampinganFormValue>({
      formName,
    })

  const isSuccess = !formValues.isLoading && formValues.isSuccess

  const goBack = useCallback(() => {
    if (window.history.state.idx <= 1)
      router.push(
        `${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja`
      )
    else router.back()
  }, [router])

  useEffect(() => {
    if (
      formValues.psSummaryHookResult.summaryFetchStatus === 'success' &&
      !formValues.isLoading &&
      !formValues.isSuccess
    ) {
      throw new InternalServerError(
        'API_PATH',
        new Error('Failed to fetch summary or form values')
      )
    }

    if (formValues.isSuccess) {
      if (!formValues.psSummaryHookResult.isPelaksanaanState) {
        router.replace(PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH)
      } else if (formValues.isLocked) {
        goBack()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formValues.isLoading,
    formValues.isLocked,
    formValues.isSuccess,
    formValues.psSummaryHookResult.isPelaksanaanState,
    formValues.psSummaryHookResult.summaryFetchStatus,
  ])

  useEffect(() => {
    if (isPeriodSuccess && !pelaksanaanGuard.isOpen) {
      router.replace(PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH)
    } else if (isPeriodSuccess && formValues.isSuccess) {
      setPageLoading(false)
    }
  }, [isPeriodSuccess, pelaksanaanGuard.isOpen, router, formValues.isSuccess])

  if (isPageLoading) {
    return (
      <div className="px-3 mx-auto max-w-7xl py-9">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <>
      <div className="px-3 mx-auto max-w-7xl py-9 space-y-7">
        <Header
          title={
            <div className="flex items-center gap-x-3">
              Pendampingan Satuan Pendidikan
              {formValues.isViewOnly && !formValues.isAppeal && (
                <Badge variant="success">Selesai</Badge>
              )}
              {formValues.isAppeal && (
                <Badge variant="neutral">Perubahan sedang diajukan</Badge>
              )}
            </div>
          }
        />

        <OnceLoaded loaded={isSuccess}>
          {!formValues.isViewOnly ? (
            <CardForm
              formValues={formValues}
              onSubmitSuccess={() => {
                trackEvent('pelaksanaan pegawai', 'submit form B')
                goBack()
              }}
            />
          ) : (
            <CardFormPreview
              formValues={formValues}
              formName={formName}
              showAppealButton
            />
          )}
        </OnceLoaded>
      </div>
    </>
  )
}

export default PendampinganSatuanPendidikan
